// frontend/src/pages/PayrollGuide/components/NexaCta.tsx
// Dark gradient banner inviting the user to ask Nexa for help,
// plus the "Contact us" link below it. Paired visually and functionally.

import React from "react";
import { MessageCircle } from "lucide-react";
import type { SizeConfig } from "../styles";
import { C, FONT } from "../styles";

interface NexaCtaProps {
  sz: SizeConfig;
  /** When true, uses column layout (avatar above text) for narrow side-panel rendering. */
  embeddedInPanel: boolean;
  /** Called when "Ask Nexa" is clicked. Parent fires the AI prompt. */
  onAskAboutPayroll: () => void;
  /** Called when "Contact us" is clicked. Parent fires the AI prompt. */
  onContactUs: () => void;
}

export default function NexaCta({
  sz,
  embeddedInPanel,
  onAskAboutPayroll,
  onContactUs,
}: NexaCtaProps): React.ReactElement {
  return (
    <>
      {/* Dark banner */}
      <div
        style={{
          background: "linear-gradient(135deg, #0A1A1E, #12262B)",
          color: "#fff",
          borderRadius: 14,
          padding: sz.nexaPad,
          marginTop: 22,
          display: "flex",
          flexDirection: embeddedInPanel ? "column" : "row",
          alignItems: "center",
          gap: embeddedInPanel ? 12 : 20,
          textAlign: embeddedInPanel ? "center" : "left",
          boxShadow: "0 4px 16px rgba(18,38,43,0.18)",
        }}
      >
        <div
          style={{
            width: sz.nexaAvatar,
            height: sz.nexaAvatar,
            borderRadius: "50%",
            background: "linear-gradient(135deg, " + C.teal + ", #0EA5E9)",
            display: "grid",
            placeItems: "center",
            flex: "0 0 " + sz.nexaAvatar + "px",
            fontSize: 18,
            fontWeight: 700,
            color: "#fff",
            letterSpacing: "-0.02em",
          }}
        >
          N
        </div>
        <div style={{ flex: 1 }}>
          <h3 style={{ fontSize: sz.nexaH3, fontWeight: 600, marginBottom: 3, color: "#fff" }}>
            Still have questions?
          </h3>
          <p style={{ fontSize: sz.nexaP, color: "rgba(255,255,255,0.7)", margin: 0 }}>
            Nexa, your Novala assistant, can answer payroll questions in plain language and walk you through anything in the app.
          </p>
        </div>
        <button
          onClick={onAskAboutPayroll}
          style={{
            background: C.teal,
            color: "#fff",
            border: "none",
            borderRadius: 9,
            padding: sz.nexaBtnPad,
            fontWeight: 600,
            fontSize: sz.nexaBtnFont,
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            fontFamily: FONT,
            flex: "0 0 auto",
            boxShadow: "0 2px 8px rgba(21,160,140,0.28)",
          }}
        >
          <MessageCircle size={14} /> Ask Nexa
        </button>
      </div>

      {/* Contact us footer */}
      <div style={{ textAlign: "center", fontSize: 13, color: C.muted, marginTop: 18 }}>
        Or{" "}
        <button
          onClick={onContactUs}
          style={{
            background: "none",
            border: "none",
            color: C.tealInk,
            fontWeight: 600,
            cursor: "pointer",
            fontFamily: FONT,
            fontSize: 13,
            textDecoration: "underline",
          }}
        >
          Contact us
        </button>{" "}
        for support that needs a human.
      </div>
    </>
  );
}